import { ChangeEventHandler, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SelectGroup, RadioGroup, Button, NumericInputGroup } from '@/common'
import { CarbonTaxPaidRaw } from '@/api/EmissionReportModel'
import { UnitLabel } from '@/shared/config/config'
import { useUpdateEmissionReport } from '@/hooks/api/emissionReports'
import { Currency, useGetCarbonPriceMechanisms } from '@/shared/hooks/api/appConfig'

export default function CarbonTaxPaidReporting({
  close,
  initialValue,
  reportId
}: {
  initialValue: CarbonTaxPaidRaw | null
  close: () => void
  reportId: string
}) {
  const { t } = useTranslation()
  const defaultCurrency = Currency.CNY
  const { data: carbonPriceMechanisms } = useGetCarbonPriceMechanisms()
  const { mutateAsync: updateEmissionReport, isPending: isUpdatingEmissionReport } =
    useUpdateEmissionReport(reportId)
  const [hasPaidCarbonTax, setHasPaidCarbonTax] = useState<boolean | null>(
    initialValue?.carbonPriceMechanismId ? true : false
  )

  const [carbonTaxPaidState, setCarbonTaxPaidState] = useState(
    initialValue || {
      currency: defaultCurrency,
      effectiveCarbonPriceDue: '',
      carbonPriceMechanismId: null,
      exchangeRate: '',
      embeddedEmissionsCovered: '',
      embeddedEmissionsCoveredByRebate: ''
    }
  )

  const onCarbonPriceMechanismsChange = useCallback(
    (id?: string) => {
      let currency: Currency
      let selectedId: number | null

      if (!id) {
        selectedId = null
        currency = defaultCurrency
      } else {
        selectedId = Number(id)

        const selectedMechanism = (carbonPriceMechanisms || []).filter(
          mechanism => mechanism.id === selectedId
        )
        currency = selectedMechanism[0]?.currency || defaultCurrency
      }

      setCarbonTaxPaidState(prev => ({
        ...prev,
        carbonPriceMechanismId: selectedId,
        currency: currency
      }))
    },
    [carbonPriceMechanisms, defaultCurrency]
  )

  const onValueChange: ChangeEventHandler<HTMLInputElement> = useCallback(evt => {
    setCarbonTaxPaidState(prev => ({
      ...prev,
      [evt.target.name]: evt.target.value
    }))
  }, [])

  const isNextEnabled = Boolean(
    hasPaidCarbonTax === false ||
      (carbonTaxPaidState.carbonPriceMechanismId &&
        carbonTaxPaidState.currency &&
        carbonTaxPaidState.effectiveCarbonPriceDue.length > 0 &&
        carbonTaxPaidState.embeddedEmissionsCovered.length > 0 &&
        carbonTaxPaidState.embeddedEmissionsCoveredByRebate.length > 0)
  )

  const onNext = useCallback(async () => {
    if (!hasPaidCarbonTax || !carbonTaxPaidState.carbonPriceMechanismId) {
      await updateEmissionReport({
        carbonTaxPaidRaw: {
          currency: defaultCurrency,
          effectiveCarbonPriceDue: '',
          carbonPriceMechanismId: null,
          exchangeRate: '',
          embeddedEmissionsCovered: '',
          embeddedEmissionsCoveredByRebate: ''
        }
      })
    } else {
      await updateEmissionReport({
        carbonTaxPaidRaw: carbonTaxPaidState
      })
    }

    close()
  }, [updateEmissionReport, hasPaidCarbonTax, carbonTaxPaidState, close])

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-slate-500">{t('report:carbonTaxPaid.fields.hasPaidCarbonTax.label')}</p>
        <RadioGroup<boolean>
          name="hasPaidCarbonTax"
          value={hasPaidCarbonTax || false}
          onChange={value => setHasPaidCarbonTax(value)}
          options={[
            { label: t('common:yes'), value: true },
            { label: t('common:no'), value: false }
          ]}
        />
      </div>
      <div className="space-y-2">
        <SelectGroup
          label={t('report:carbonTaxPaid.fields.carbonPriceMechanism.label')}
          disabled={!hasPaidCarbonTax}
          value={
            carbonTaxPaidState.carbonPriceMechanismId
              ? String(carbonTaxPaidState.carbonPriceMechanismId)
              : undefined
          }
          options={(carbonPriceMechanisms || []).map(mechanism => ({
            label: t(`common:carbonPriceMechanisms.${mechanism.id}`),
            value: String(mechanism.id)
          }))}
          onChange={e => onCarbonPriceMechanismsChange(e.target.value)}
        />
        <NumericInputGroup
          name="effectiveCarbonPriceDue"
          disabled={!hasPaidCarbonTax}
          label={t('report:carbonTaxPaid.fields.effectiveCarbonPriceDue.label')}
          value={carbonTaxPaidState.effectiveCarbonPriceDue}
          onChange={onValueChange}
          type="text"
          suffix={carbonTaxPaidState.currency}
          numericFormat={{
            decimalScale: 3,
            allowNegative: false
          }}
        />
        <NumericInputGroup
          name="embeddedEmissionsCovered"
          disabled={!hasPaidCarbonTax}
          label={t('report:carbonTaxPaid.fields.embeddedEmissionsCovered.label')}
          value={carbonTaxPaidState.embeddedEmissionsCovered}
          onChange={onValueChange}
          type="text"
          suffix={UnitLabel.tCO2e}
          numericFormat={{
            decimalScale: 3,
            allowNegative: false
          }}
        />
        <NumericInputGroup
          name="embeddedEmissionsCoveredByRebate"
          disabled={!hasPaidCarbonTax}
          label={t('report:carbonTaxPaid.fields.embeddedEmissionsCoveredByRebate.label')}
          value={carbonTaxPaidState.embeddedEmissionsCoveredByRebate}
          onChange={onValueChange}
          type="text"
          suffix={UnitLabel.tCO2e}
          numericFormat={{
            decimalScale: 3,
            allowNegative: false
          }}
        />
      </div>
      <footer className="gap-2 flex justify-end">
        <Button className="min-w-24" onClick={close} variant="neutral">
          {t('common:actions:cancel')}
        </Button>
        <Button
          className="min-w-24"
          onClick={onNext}
          isLoading={isUpdatingEmissionReport}
          disabled={!isNextEnabled || isUpdatingEmissionReport}
        >
          {t('common:actions:save')}
        </Button>
      </footer>
    </div>
  )
}
